// src/pages/finance-portal/invoice-templates/InvoiceTemplates.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Card, CardContent, CardActions,
    Grid, Chip, IconButton, Alert, CircularProgress, Skeleton,
    MenuItem, Select, FormControl, InputLabel, FormControlLabel, Checkbox,
    Dialog, DialogTitle, DialogContent, DialogActions,
    Tooltip, Collapse, InputAdornment,
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SettingsIcon from '@mui/icons-material/Settings';
import axiosInstance from '../../../utils/axiosInstance';
import { useSnackbar } from 'notistack';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const LAYOUT_OPTIONS = [
    { value: 'standard', label: 'Standard' },
    { value: 'modern', label: 'Modern' },
    { value: 'minimal', label: 'Minimal' },
    { value: 'classic', label: 'Classic' },
];

const FONT_OPTIONS = [
    { value: 'Plus Jakarta Sans', label: 'Plus Jakarta Sans' },
    { value: 'Inter', label: 'Inter' },
    { value: 'Roboto', label: 'Roboto' },
    { value: 'Open Sans', label: 'Open Sans' },
];

const EMPTY_FORM = {
    name: '',
    layout: 'standard',
    primary_color: '#22c55e',
    secondary_color: '#1e293b',
    font_family: 'Plus Jakarta Sans',
    logo_url: '',
    header_text: '',
    footer_text: '',
    custom_css: '',
    is_default: false,
};

export default function InvoiceTemplates() {
    const { enqueueSnackbar } = useSnackbar();

    const [templates, setTemplates] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Dialog state
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editTemplate, setEditTemplate] = useState(null);
    const [formData, setFormData] = useState({ ...EMPTY_FORM });
    const [saving, setSaving] = useState(false);
    const [showAdvanced, setShowAdvanced] = useState(false);

    // Delete dialog
    const [deleteDialog, setDeleteDialog] = useState({ open: false, template: null });
    const [deleting, setDeleting] = useState(false);

    const fetchTemplates = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const res = await axiosInstance.get('/api/finance/invoice-templates');
            if (res.data.success) {
                setTemplates(res.data.data || []);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load templates');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

    const openCreateDialog = () => {
        setEditTemplate(null);
        setFormData({ ...EMPTY_FORM });
        setShowAdvanced(false);
        setDialogOpen(true);
    };

    const openEditDialog = (template) => {
        setEditTemplate(template);
        const config = template.template_config || {};
        setFormData({
            name: template.name || '',
            layout: config.layout || 'standard',
            primary_color: config.primary_color || '#22c55e',
            secondary_color: config.secondary_color || '#1e293b',
            font_family: config.font_family || 'Plus Jakarta Sans',
            logo_url: config.logo_url || '',
            header_text: config.header_text || '',
            footer_text: config.footer_text || '',
            custom_css: config.custom_css || '',
            is_default: template.is_default || false,
        });
        setShowAdvanced(!!config.custom_css);
        setDialogOpen(true);
    };

    const handleSave = async () => {
        if (!formData.name.trim()) {
            setError('Template name is required');
            return;
        }

        setSaving(true);
        setError(null);
        try {
            const payload = {
                name: formData.name,
                is_default: formData.is_default,
                template_config: {
                    layout: formData.layout,
                    primary_color: formData.primary_color,
                    secondary_color: formData.secondary_color,
                    font_family: formData.font_family,
                    logo_url: formData.logo_url,
                    header_text: formData.header_text,
                    footer_text: formData.footer_text,
                    custom_css: formData.custom_css,
                },
            };

            if (editTemplate) {
                await axiosInstance.put(`/api/finance/invoice-templates/${editTemplate.id}`, payload);
                enqueueSnackbar('Template updated successfully', { variant: 'success' });
            } else {
                await axiosInstance.post('/api/finance/invoice-templates', payload);
                enqueueSnackbar('Template created successfully', { variant: 'success' });
            }
            setDialogOpen(false);
            setEditTemplate(null);
            fetchTemplates();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save template');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        setDeleting(true);
        try {
            await axiosInstance.delete(`/api/finance/invoice-templates/${deleteDialog.template.id}`);
            setDeleteDialog({ open: false, template: null });
            enqueueSnackbar('Template deleted successfully', { variant: 'success' });
            fetchTemplates();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete template');
        } finally {
            setDeleting(false);
        }
    };

    const handleSetDefault = async (template) => {
        try {
            await axiosInstance.put(`/api/finance/invoice-templates/${template.id}`, {
                name: template.name,
                is_default: true,
                template_config: template.template_config || {},
            });
            enqueueSnackbar(`"${template.name}" set as default template`, { variant: 'success' });
            fetchTemplates();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to set default template');
        }
    };

    const getPreviewColor = (template) => {
        const config = template.template_config || {};
        return config.primary_color || '#22c55e';
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <DescriptionIcon sx={{ fontSize: 28, color: '#fff' }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' } }}>Invoice Templates</Typography>
                        <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>Customize the look of your invoices</Typography>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={openCreateDialog}
                        sx={{
                            fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                            bgcolor: 'rgba(255,255,255,0.2)', color: '#fff',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                            backdropFilter: 'blur(10px)',
                        }}
                    >
                        Create Template
                    </Button>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}

                {/* Template Grid */}
                {isLoading ? (
                    <Grid container spacing={3}>
                        {[1, 2, 3].map(i => (
                            <Grid item xs={12} sm={6} md={4} key={i}>
                                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                                    <Skeleton variant="rectangular" height={160} />
                                    <CardContent>
                                        <Skeleton width="60%" height={24} />
                                        <Skeleton width="40%" height={20} sx={{ mt: 1 }} />
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                ) : templates.length === 0 ? (
                    <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', textAlign: 'center', py: 6 }}>
                        <CardContent>
                            <DescriptionIcon sx={{ fontSize: 56, color: '#cbd5e1', mb: 2 }} />
                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem', color: '#1e293b', mb: 1 }}>
                                No templates yet
                            </Typography>
                            <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#64748b', mb: 3 }}>
                                Create your first invoice template to customize how your invoices look.
                            </Typography>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={openCreateDialog}
                                sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}
                            >
                                Create Template
                            </Button>
                        </CardContent>
                    </Card>
                ) : (
                    <Grid container spacing={3}>
                        {templates.map(template => {
                            const previewColor = getPreviewColor(template);
                            const config = template.template_config || {};
                            const isSystem = template.is_system || false;

                            return (
                                <Grid item xs={12} sm={6} md={4} key={template.id}>
                                    <Card sx={{
                                        borderRadius: '16px',
                                        border: template.is_default ? `2px solid ${ACCENT}` : '1px solid #e2e8f0',
                                        boxShadow: 'none',
                                        transition: 'all 0.2s',
                                        '&:hover': { boxShadow: '0 4px 20px rgba(0,0,0,0.08)', transform: 'translateY(-2px)' },
                                        overflow: 'hidden',
                                    }}>
                                        {/* Preview Area */}
                                        <Box sx={{
                                            height: 160,
                                            background: `linear-gradient(135deg, ${previewColor}15 0%, ${previewColor}08 100%)`,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            position: 'relative',
                                            borderBottom: '1px solid #e2e8f0',
                                        }}>
                                            {/* Mini invoice preview */}
                                            <Box sx={{
                                                width: '70%',
                                                height: '80%',
                                                bgcolor: '#fff',
                                                borderRadius: '8px',
                                                boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                                                p: 2,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                overflow: 'hidden',
                                            }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                    <Box sx={{ width: 30, height: 6, borderRadius: 3, bgcolor: previewColor }} />
                                                    <Box sx={{ width: 20, height: 4, borderRadius: 2, bgcolor: '#e2e8f0' }} />
                                                </Box>
                                                <Box sx={{ width: '60%', height: 4, borderRadius: 2, bgcolor: '#e2e8f0', mb: 0.5 }} />
                                                <Box sx={{ width: '40%', height: 3, borderRadius: 2, bgcolor: '#f1f5f9', mb: 1.5 }} />
                                                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                    <Box sx={{ width: '100%', height: 3, borderRadius: 2, bgcolor: `${previewColor}20` }} />
                                                    <Box sx={{ width: '100%', height: 3, borderRadius: 2, bgcolor: '#f8fafc' }} />
                                                    <Box sx={{ width: '100%', height: 3, borderRadius: 2, bgcolor: '#f8fafc' }} />
                                                    <Box sx={{ width: '100%', height: 3, borderRadius: 2, bgcolor: '#f8fafc' }} />
                                                </Box>
                                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                                                    <Box sx={{ width: 24, height: 5, borderRadius: 2, bgcolor: previewColor }} />
                                                </Box>
                                            </Box>

                                            {/* Badges */}
                                            <Box sx={{ position: 'absolute', top: 10, right: 10, display: 'flex', gap: 0.5 }}>
                                                {template.is_default && (
                                                    <Chip
                                                        icon={<StarIcon sx={{ fontSize: 14, color: '#92400e !important' }} />}
                                                        label="Default"
                                                        size="small"
                                                        sx={{ fontFamily, fontSize: '0.65rem', fontWeight: 600, height: 22, bgcolor: '#fef3c7', color: '#92400e' }}
                                                    />
                                                )}
                                                {isSystem && (
                                                    <Chip
                                                        label="System"
                                                        size="small"
                                                        sx={{ fontFamily, fontSize: '0.65rem', fontWeight: 600, height: 22, bgcolor: '#e2e8f0', color: '#64748b' }}
                                                    />
                                                )}
                                            </Box>
                                        </Box>

                                        <CardContent sx={{ pb: 1 }}>
                                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', color: '#1e293b', mb: 0.5 }}>
                                                {template.name}
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Chip
                                                    label={config.layout || 'standard'}
                                                    size="small"
                                                    sx={{ fontFamily, fontSize: '0.65rem', fontWeight: 500, height: 20, bgcolor: '#f1f5f9', color: '#64748b', textTransform: 'capitalize' }}
                                                />
                                                <Box sx={{
                                                    width: 14, height: 14, borderRadius: '50%',
                                                    bgcolor: previewColor,
                                                    border: '2px solid #fff',
                                                    boxShadow: '0 0 0 1px #e2e8f0',
                                                }} />
                                                {config.secondary_color && (
                                                    <Box sx={{
                                                        width: 14, height: 14, borderRadius: '50%',
                                                        bgcolor: config.secondary_color,
                                                        border: '2px solid #fff',
                                                        boxShadow: '0 0 0 1px #e2e8f0',
                                                        ml: -0.5,
                                                    }} />
                                                )}
                                                <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8', ml: 'auto' }}>
                                                    {config.font_family || 'Plus Jakarta Sans'}
                                                </Typography>
                                            </Box>
                                        </CardContent>

                                        <CardActions sx={{ px: 2, pb: 1.5, pt: 0 }}>
                                            <Tooltip title="Edit">
                                                <IconButton size="small" onClick={() => openEditDialog(template)} sx={{ color: '#94a3b8', '&:hover': { color: '#3b82f6' } }}>
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            {!isSystem && (
                                                <Tooltip title="Delete">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => setDeleteDialog({ open: true, template })}
                                                        sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444' } }}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {!template.is_default && (
                                                <Tooltip title="Set as Default">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleSetDefault(template)}
                                                        sx={{ color: '#94a3b8', '&:hover': { color: '#f59e0b' } }}
                                                    >
                                                        <StarBorderIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </CardActions>
                                    </Card>
                                </Grid>
                            );
                        })}
                    </Grid>
                )}
            </Box>

            {/* Create/Edit Dialog */}
            <Dialog
                open={dialogOpen}
                onClose={() => { setDialogOpen(false); setEditTemplate(null); }}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: '16px' } }}
            >
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>
                    {editTemplate ? `Edit "${editTemplate.name}"` : 'Create Invoice Template'}
                </DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: '16px !important' }}>
                    {/* Name */}
                    <TextField
                        label="Template Name"
                        size="small"
                        required
                        fullWidth
                        value={formData.name}
                        onChange={e => setFormData({ ...formData, name: e.target.value })}
                        InputLabelProps={{ sx: { fontFamily } }}
                        InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                        placeholder="e.g., Professional Green"
                    />

                    {/* Layout */}
                    <FormControl size="small" fullWidth>
                        <InputLabel sx={{ fontFamily }}>Layout</InputLabel>
                        <Select
                            value={formData.layout}
                            onChange={e => setFormData({ ...formData, layout: e.target.value })}
                            label="Layout"
                            sx={{ fontFamily, borderRadius: '10px' }}
                        >
                            {LAYOUT_OPTIONS.map(opt => (
                                <MenuItem key={opt.value} value={opt.value} sx={{ fontFamily }}>{opt.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Colors row */}
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={{ fontFamily, fontSize: '0.75rem', fontWeight: 600, color: '#64748b', mb: 0.5 }}>Primary Color</Typography>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                <Box
                                    component="input"
                                    type="color"
                                    value={formData.primary_color}
                                    onChange={e => setFormData({ ...formData, primary_color: e.target.value })}
                                    sx={{
                                        width: 40, height: 36, border: '2px solid #e2e8f0', borderRadius: '8px',
                                        cursor: 'pointer', padding: '2px', background: 'none',
                                        '&::-webkit-color-swatch-wrapper': { padding: 0 },
                                        '&::-webkit-color-swatch': { border: 'none', borderRadius: '5px' },
                                    }}
                                />
                                <TextField
                                    size="small"
                                    value={formData.primary_color}
                                    onChange={e => setFormData({ ...formData, primary_color: e.target.value })}
                                    InputProps={{ sx: { fontFamily, fontSize: '0.85rem', borderRadius: '10px' } }}
                                    sx={{ flex: 1 }}
                                />
                            </Box>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={{ fontFamily, fontSize: '0.75rem', fontWeight: 600, color: '#64748b', mb: 0.5 }}>Secondary Color</Typography>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                <Box
                                    component="input"
                                    type="color"
                                    value={formData.secondary_color}
                                    onChange={e => setFormData({ ...formData, secondary_color: e.target.value })}
                                    sx={{
                                        width: 40, height: 36, border: '2px solid #e2e8f0', borderRadius: '8px',
                                        cursor: 'pointer', padding: '2px', background: 'none',
                                        '&::-webkit-color-swatch-wrapper': { padding: 0 },
                                        '&::-webkit-color-swatch': { border: 'none', borderRadius: '5px' },
                                    }}
                                />
                                <TextField
                                    size="small"
                                    value={formData.secondary_color}
                                    onChange={e => setFormData({ ...formData, secondary_color: e.target.value })}
                                    InputProps={{ sx: { fontFamily, fontSize: '0.85rem', borderRadius: '10px' } }}
                                    sx={{ flex: 1 }}
                                />
                            </Box>
                        </Box>
                    </Box>

                    {/* Font Family */}
                    <FormControl size="small" fullWidth>
                        <InputLabel sx={{ fontFamily }}>Font Family</InputLabel>
                        <Select
                            value={formData.font_family}
                            onChange={e => setFormData({ ...formData, font_family: e.target.value })}
                            label="Font Family"
                            sx={{ fontFamily, borderRadius: '10px' }}
                        >
                            {FONT_OPTIONS.map(opt => (
                                <MenuItem key={opt.value} value={opt.value} sx={{ fontFamily: opt.value }}>{opt.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Logo URL */}
                    <TextField
                        label="Logo URL"
                        size="small"
                        fullWidth
                        value={formData.logo_url}
                        onChange={e => setFormData({ ...formData, logo_url: e.target.value })}
                        InputLabelProps={{ sx: { fontFamily } }}
                        InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                        placeholder="https://example.com/logo.png"
                    />

                    {/* Header Text */}
                    <TextField
                        label="Header Text"
                        size="small"
                        fullWidth
                        multiline
                        rows={2}
                        value={formData.header_text}
                        onChange={e => setFormData({ ...formData, header_text: e.target.value })}
                        InputLabelProps={{ sx: { fontFamily } }}
                        InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                        placeholder="Custom text displayed in the invoice header..."
                    />

                    {/* Footer Text */}
                    <TextField
                        label="Footer Text"
                        size="small"
                        fullWidth
                        multiline
                        rows={2}
                        value={formData.footer_text}
                        onChange={e => setFormData({ ...formData, footer_text: e.target.value })}
                        InputLabelProps={{ sx: { fontFamily } }}
                        InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                        placeholder="Thank you for your business! Payment terms..."
                    />

                    {/* Advanced: Custom CSS (collapsible) */}
                    <Box>
                        <Button
                            size="small"
                            onClick={() => setShowAdvanced(!showAdvanced)}
                            startIcon={<SettingsIcon fontSize="small" />}
                            endIcon={showAdvanced ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                            sx={{ fontFamily, textTransform: 'none', color: '#64748b', fontSize: '0.8rem' }}
                        >
                            Advanced Options
                        </Button>
                        <Collapse in={showAdvanced}>
                            <TextField
                                label="Custom CSS"
                                size="small"
                                fullWidth
                                multiline
                                rows={4}
                                value={formData.custom_css}
                                onChange={e => setFormData({ ...formData, custom_css: e.target.value })}
                                InputLabelProps={{ sx: { fontFamily } }}
                                InputProps={{
                                    sx: {
                                        fontFamily: 'monospace', fontSize: '0.8rem', borderRadius: '10px',
                                        bgcolor: '#f8fafc',
                                    },
                                }}
                                placeholder=".invoice-header { ... }"
                                sx={{ mt: 1 }}
                            />
                        </Collapse>
                    </Box>

                    {/* Is Default */}
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={formData.is_default}
                                onChange={e => setFormData({ ...formData, is_default: e.target.checked })}
                                sx={{ color: '#cbd5e1', '&.Mui-checked': { color: ACCENT } }}
                            />
                        }
                        label={
                            <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                Set as default template for new invoices
                            </Typography>
                        }
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button
                        onClick={() => { setDialogOpen(false); setEditTemplate(null); }}
                        sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={saving || !formData.name.trim()}
                        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}
                    >
                        {saving ? 'Saving...' : editTemplate ? 'Save Changes' : 'Create Template'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation */}
            <Dialog
                open={deleteDialog.open}
                onClose={() => setDeleteDialog({ open: false, template: null })}
                PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}
            >
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Delete "{deleteDialog.template?.name}"?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily, color: '#64748b' }}>
                        This will permanently delete this invoice template. Invoices that were created using this template will not be affected.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setDeleteDialog({ open: false, template: null })} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleDelete}
                        disabled={deleting}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' } }}
                    >
                        {deleting ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
